import { nextTick } from "vue";
import type { Ref } from "vue";
import type { Router } from "vue-router";
import type { Store } from "vuex";
import {
  OperationInput,
  DeleteScheduledDeliveryMutation,
  DeleteScheduledDeliveryMutationVariables,
  DeleteScheduledDeliveryDocument,
} from "@/generated/graphql";
import { generateApolloClient } from "@/common/apolloClientUtil";
import { routerPath } from "@/common/router";
import scheduledDeliveryStore from "@/store/scheduledDeliveryStore";
import searchConditionStore from "@/store/searchConditionStore";
import type { ListData } from "../types";
import { SCHEDULED_DELIVERY_LIST_FILE_NAME } from "../constants";

/**
 * 画面の操作イベントに関するハンドラをまとめて提供する composable。
 *
 * @returns 画面イベントから呼び出す操作ハンドラ群
 */
export const useScheduledDeliveryActions = (params: {
  selectListCheckbox: Ref<ListData[]>;
  confirmDeleteDialog: Ref<boolean>;
  overlay: Ref<boolean>;
  errdialog: Ref<boolean>;
  errMessage: Ref<string>;
  chainSelectValues: Ref<{ chain_code: string; name: string } | null>;
  shopSelectFromValues: Ref<{ shop_code: string; name: string }>;
  shopSelectToValues: Ref<{ shop_code: string; name: string }>;
  operationInput: OperationInput;
  searchQuery: () => Promise<void>;
  router: Router;
  store: Store<unknown>;
}) => {
  /**
   * 検索条件を保持する
   */
  const saveSearchCondition = () => {
    searchConditionStore.setCondition({
      view_name: SCHEDULED_DELIVERY_LIST_FILE_NAME,
      conditions: new Map()
        .set("chain_code", params.chainSelectValues.value?.chain_code)
        .set("chain_name", params.chainSelectValues.value?.name)
        .set("shop_code_from", params.shopSelectFromValues.value.shop_code)
        .set("shop_code_from_name", params.shopSelectFromValues.value.name)
        .set("shop_code_to", params.shopSelectToValues.value.shop_code)
        .set("shop_code_to_name", params.shopSelectToValues.value.name),
    });
  };

  /**
   * 編集ボタン @click
   */
  const onClickEdit = async (item: ListData): Promise<void> => {
    //検索条件を保持する
    saveSearchCondition();

    // 「店舗間定期配達登録-入力」画面へ遷移
    await scheduledDeliveryStore.setupValue({
      chain_code: item.chainCode,
      shop_code_from: item.shopCodeFrom,
      shop_code_to: item.shopCodeTo,
      scheduled_delivery_no: item.scheduled_delivery_no,
      processFlag: 1,
    });
    await params.router.push({ path: routerPath.scheduledDeliveryRegister });
    params.store.commit("listEditOn");
  };

  /**
   * 店舗削除アイコン @Click
   */
  const onClickDelete = async (item: ListData): Promise<void> => {
    //検索条件を保持する
    saveSearchCondition();

    // 「店舗間定期配達登録-入力」画面へ遷移
    await scheduledDeliveryStore.setupValue({
      chain_code: item.chainCode,
      shop_code_from: item.shopCodeFrom,
      shop_code_to: item.shopCodeTo,
      scheduled_delivery_no: item.scheduled_delivery_no,
      processFlag: 2,
    });
    await params.router.push({ path: routerPath.scheduledDeliveryRegister });
    params.store.commit("listEditOn");
  };

  /**
   * 一括削除 @Click
   */
  const onClickCheckDelete = async (): Promise<void> => {
    if (params.selectListCheckbox.value.length === 0) {
      return;
    }
    params.confirmDeleteDialog.value = true;
  };

  /**
   * 削除処理GraphQL実行(1件ずつ)
   */
  const deleteExecute = async (
    scheduled_delivery_no: string
  ): Promise<boolean> => {
    //graphql接続
    const apolloClient = await generateApolloClient();

    // 配達依頼削除
    try {
      await apolloClient.mutate<
        DeleteScheduledDeliveryMutation,
        DeleteScheduledDeliveryMutationVariables
      >({
        mutation: DeleteScheduledDeliveryDocument,
        variables: {
          scheduled_delivery_no: scheduled_delivery_no,
          logInput: params.operationInput,
        },
      });
      return true;
    } catch (err: unknown) {
      console.error("err=", err);
      params.errdialog.value = true;
      if (err instanceof Error) {
        params.errMessage.value = "店舗間定期配達データの削除に失敗しました。";
      } else {
        params.errMessage.value = "予期せぬエラーが発生しました。";
      }
      return false;
    }
  };

  /**
   * 一括削除 実行 @Click
   */
  const onClickCheckDeleteComplete = async (): Promise<void> => {
    let arrayError: string[] = [];

    try {
      params.overlay.value = true;

      params.operationInput.detail = "操作詳細";
      params.operationInput.display_category = "店舗間定期配達一括削除";
      params.operationInput.operation = "店舗間定期配達一括削除";

      //1件ずつ削除する
      for (let val of params.selectListCheckbox.value) {
        if (val !== undefined && val !== null) {
          //データを削除する
          if (!(await deleteExecute(val.scheduled_delivery_no))) {
            arrayError.push(val.scheduled_delivery_no);
          }
        }
      }

      // 選択内容クリア
      params.selectListCheckbox.value = [];

      //画面を閉じる
      params.confirmDeleteDialog.value = false;
      params.overlay.value = false;

      //再検索する
      await nextTick(async () => {
        await params.searchQuery();
      });
    } catch (err: unknown) {
      console.error("err=", err);
      params.errdialog.value = true;
      if (err instanceof Error) {
        params.errMessage.value =
          "受付番号 " + arrayError.join(",") + "の削除に失敗しました。";
      } else {
        params.errMessage.value =
          "受付番号 " +
          arrayError.join(",") +
          "の削除中に予期せぬエラーが発生しました。";
      }
    } finally {
      params.overlay.value = false;
    }
  };

  return {
    onClickEdit,
    onClickDelete,
    onClickCheckDelete,
    onClickCheckDeleteComplete,
  };
};
